import type { ReactNode } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { TicketIcon } from '@gto/icons-react/outline/ticket'
import { Divider } from '@gto/ui/divider'
import { Paper } from '@gto/ui/paper'
import { Skeleton } from '@gto/ui/skeleton'
import { Typography } from '@gto/ui/typography'

import type { AffiliateCouponWithStatsSchema } from '@/api/gen'
import { useGetMyCoupons } from '@/api/gen'
import { CopyButton } from '@/components/copy-button'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { StatusBadge } from '@/components/status-badge'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { toAmount } from '@/utils/to-amount'

type StatRowProps = {
  icon: ReactNode
  label: ReactNode
  value: ReactNode
}

const StatRow = ({ icon, label, value }: StatRowProps) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Typography color="text.secondary">{icon}</Typography>
      <Typography
        color="text.secondary"
        variant="body2"
      >
        {label}
      </Typography>
    </div>
    {value}
  </div>
)

const CouponCard = ({ coupon }: { coupon: AffiliateCouponWithStatsSchema }) => {
  const stats = coupon.stats

  return (
    <Paper
      className="flex flex-[1_1_240px] flex-col gap-4 rounded-md p-5"
      elevation="2"
    >
      {/* Code hero */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-1">
          <Typography
            className="font-semibold"
            variant="h6"
          >
            {coupon.code}
          </Typography>
          <CopyButton text={coupon.code} />
        </div>

        {!coupon.is_active && <StatusBadge status="INACTIVE" />}
      </div>

      <Divider />

      {/* Stats */}
      <div className="flex flex-col gap-4">
        <StatRow
          label={<FormattedMessage id="coupons.totalOrders" />}
          value={<Typography variant="body2">{stats.total_orders}</Typography>}
          icon={
            <ShoppingCartIcon
              height={16}
              width={16}
            />
          }
        />
        <StatRow
          label={<FormattedMessage id="coupons.totalRevenue" />}
          icon={
            <CurrencyDollarIcon
              height={16}
              width={16}
            />
          }
          value={
            <Typography variant="body2">
              <FormattedNumber
                currency="USD"
                style="currency"
                value={toAmount(stats.total_revenue_usd, 'coupons total_revenue_usd:')}
              />
            </Typography>
          }
        />
        <StatRow
          label={<FormattedMessage id="coupons.commissionEarned" />}
          icon={
            <BanknotesIcon
              height={16}
              width={16}
            />
          }
          value={
            <Typography
              color="primary"
              variant="body2"
            >
              <FormattedNumber
                currency="USD"
                style="currency"
                value={toAmount(stats.total_commission_usd, 'coupons total_commission_usd:')}
              />
            </Typography>
          }
        />
      </div>
    </Paper>
  )
}

const CouponCardSkeleton = () => (
  <Paper
    className="flex flex-[1_1_240px] flex-col gap-4 rounded-md p-5"
    elevation="2"
  >
    <div className="flex flex-col items-center gap-2">
      <Skeleton
        height={40}
        width={220}
      />
    </div>
    <Divider />
    <div className="flex flex-col gap-4">
      <Skeleton
        height={22}
        width="100%"
      />
      <Skeleton
        height={22}
        width="100%"
      />
      <Skeleton
        height={22}
        width="100%"
      />
    </div>
  </Paper>
)

type CouponsGridProps = {
  coupons: AffiliateCouponWithStatsSchema[]
  loading: boolean
}

const CouponsGrid = ({ coupons, loading }: CouponsGridProps) => {
  if (loading) {
    return (
      <div
        className="flex flex-row flex-wrap gap-4"
        data-tst="coupons-skeleton-grid"
      >
        {Array.from({ length: 4 }, (_, i) => (
          <CouponCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (coupons.length === 0) {
    return <EmptyState title={<FormattedMessage id="coupons.empty" />} />
  }

  return (
    <div
      className="flex flex-row flex-wrap gap-4"
      data-tst="coupons-grid"
    >
      {coupons.map((coupon) => (
        <CouponCard
          key={coupon.id}
          coupon={coupon}
        />
      ))}
    </div>
  )
}

export const CouponsPage = () => {
  const couponsQuery = useGetMyCoupons()
  const { data: coupons, isLoading, isError, refetch } = couponsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  useReportQueryError(couponsQuery, 'getMyCoupons failed: ')

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="coupons.title" />} />

      <Page.Content>
        {isError ? (
          <InlineQueryError
            data-tst="coupons-error"
            messageId="coupons.error"
            onRetry={() => void refetch()}
          />
        ) : (
          <StatsCard
            icon={<TicketIcon />}
            title={<FormattedMessage id="coupons.listTitle" />}
          >
            <CouponsGrid
              coupons={coupons ?? []}
              loading={isLoading}
            />
          </StatsCard>
        )}
      </Page.Content>
    </Page>
  )
}
